/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Section } from '../../components/Section';
import { Paper, TextField, Button, FormControl, Select, MenuItem, InputLabel, FormControlLabel, Checkbox, OutlinedInput, InputAdornment, IconButton } from '@mui/material';
import useErrorHandler from '../../utils/userErrorHandler';
import { useSnackbar } from 'notistack';
import usePermissionLevelsQuery, { Permission } from '../AccountEditPage/gql/permissionLevels';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import useCreateUserMutation from './gql/createUser';

const AccountCreatePage: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const permissionsData = usePermissionLevelsQuery();
    const [createUser, createdUser] = useCreateUserMutation();
    useErrorHandler(permissionsData.error, createdUser.error);

    const [username, setUsername] = useState('');
    const [firstname, setFirstname] = useState('');
    const [lastname, setLastname] = useState('');
    const [middlename, setMiddlename] = useState('');
    const [email, setEmail] = useState('');
    const [active, setActive] = useState(false);
    const [permission, setPermission] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPasswords, setShowNewPasswords] = useState(false);

    const handleSave = async () => {
        const rgx = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/;
        const match = email.match(rgx);

        if (username && lastname && firstname && email && permission && newPassword && confirmPassword) {
            if (match) {
                if (newPassword.length >= 8 && confirmPassword.length >= 8) {
                    if (newPassword === confirmPassword) {
                        await createUser({
                            data: {
                                username,
                                firstname,
                                lastname,
                                middlename,
                                email,
                                password: confirmPassword,
                                active,
                                permission: parseInt(permission),
                            },
                        });
                        navigate('/settings/accounts');
                    } else {
                        enqueueSnackbar('A jelszavak nem egyeznek meg!', { variant: 'error' });
                    }
                } else {
                    enqueueSnackbar('A jelszónak legalább 8 karakter hosszúnak kell lennie!', { variant: 'error' });
                }
            } else {
                enqueueSnackbar('Az e-mail formátuma nem megfelelő!', { variant: 'error' });
            }
        } else {
            enqueueSnackbar('A csillaggal jelölt mezők kitöltése kötelező!', { variant: 'error' });
        }
    };

    const handleNameChange = (input: string, setNameState: React.Dispatch<React.SetStateAction<string>>) => {
        const formattedName = input.charAt(0).toUpperCase() + input.slice(1).toLocaleLowerCase();
        setNameState(formattedName);
    };

    return (
        <Paper css={containerStyles} elevation={0}>
            <Section title={`Fiók létrehozása`}>
                <div css={sectionStyles}>
                    <div css={dataContainerStyles}>
                        <div css={dataColStyles}>
                            <TextField required fullWidth id="lastname" label="Vezetéknév" placeholder="Vezetéknév" value={lastname} onChange={(e) => handleNameChange(e.target.value, setLastname)} css={inputsStyles} />
                            <TextField required fullWidth id="firstname" label="Keresztnév" placeholder="Keresztnév" value={firstname} onChange={(e) => handleNameChange(e.target.value, setFirstname)} css={inputsStyles} />
                            <TextField fullWidth id="middlename" label="Utónév" placeholder="Utónév" value={middlename} onChange={(e) => handleNameChange(e.target.value, setMiddlename)} css={inputsStyles} />
                            <FormControl sx={{ minWidth: 200, width: '100%' }} required>
                                <InputLabel id="permissions">Jogosultság</InputLabel>
                                <Select labelId="permissions" id="permissions-select" label="Jogosultság" value={permission} onChange={(e) => setPermission(e.target.value)}>
                                    {permissionsData.data?.map((permission: Permission) => {
                                        return (
                                            <MenuItem key={permission.id} value={permission.id}>
                                                {permission.name}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                            <FormControlLabel control={<Checkbox checked={active} onChange={() => setActive(!active)} />} label={`Állapot: ${active ? 'Aktív' : 'Inaktív'}`} />
                        </div>
                        <div css={dataColStyles}>
                            <TextField required fullWidth id="username" label="Felhasználónév" placeholder="Felhasználónév" value={username} onChange={(e) => setUsername(e.target.value)} css={inputsStyles} />
                            <TextField required fullWidth id="email" label="E-mail" placeholder="E-mail cím" value={email} onChange={(e) => setEmail(e.target.value)} css={inputsStyles} />
                            <FormControl variant="outlined" css={passwordInputStyles} required>
                                <InputLabel htmlFor="password-input">Jelszó</InputLabel>
                                <OutlinedInput
                                    id="password-input"
                                    type={showNewPasswords ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton aria-label="toggle password visibility" onClick={() => setShowNewPasswords(!showNewPasswords)} onMouseDown={(e) => e.preventDefault()} edge="end">
                                                {showNewPasswords ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    label="Jelszó"
                                />
                            </FormControl>
                            <FormControl variant="outlined" css={passwordInputStyles} required>
                                <InputLabel htmlFor="password-input">Jelszó megerősítése</InputLabel>
                                <OutlinedInput
                                    id="password-input"
                                    type={showNewPasswords ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton aria-label="toggle password visibility" onClick={() => setShowNewPasswords(!showNewPasswords)} onMouseDown={(e) => e.preventDefault()} edge="end">
                                                {showNewPasswords ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    label="Jelszó megerősítése"
                                />
                            </FormControl>
                        </div>
                    </div>
                    <div css={buttonInputsContainerStyles}>
                        <Button size="large" variant="contained" color="success" onClick={handleSave}>
                            Létrehoz
                        </Button>
                    </div>
                </div>
            </Section>
        </Paper>
    );
};

const containerStyles = css`
    max-width: 1500px;
    display: flex;
    flex-direction: column;
    padding: 0 1rem;
    margin: auto;
    margin-bottom: 25px;
    margin-top: 25px;
    gap: 25px;
`;

const sectionStyles = css`
    display: flex;
    flex-direction: column;
    padding: 20px;

    & > * + * {
        margin-top: 20px !important;
    }
`;

const dataContainerStyles = css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-evenly;
    gap: 30px;
`;

const dataColStyles = css`
    min-width: 360px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const inputsStyles = css`
    min-width: 200px;
`;

const buttonInputsContainerStyles = css`
    align-self: end;
`;

const passwordInputStyles = css`
    min-width: 300px;
`;

export default AccountCreatePage;
